import type { ReactNode } from 'react';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { PersistedGateCheckpointDto } from '../../api/employerVerificationDashboardApi';
import { GATE_ORDER_PIPE, gateMeta, type GateKey } from '../../lib/employerVerificationCopy';
import {
  formatGateStepLabel,
  resolvedCheckpointDisplayStatus,
} from '../../lib/employerVerificationGateAudit';
import { formatGateReasonReadableFlow } from '../../lib/employerVerificationGateReasonFormat';
import { comparedValuesForCheckpointStep } from '../../lib/checkpointComparedValues';

function checkpointChipColor(
  status: string,
): 'success' | 'error' | 'warning' | 'default' {
  const u = status.trim().toUpperCase();
  if (u === 'PASS') return 'success';
  if (u === 'FAIL') return 'error';
  if (u === 'REVIEW') return 'warning';
  return 'default';
}

function truncateSummary(reason: string, maxLen = 120): string {
  const t = formatGateReasonReadableFlow(reason).trim();
  if (!t) return '—';
  // Card body shows full multi-line text; this is only for tooltip compare / overflow hints.
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

type Props = {
  audit: Record<string, PersistedGateCheckpointDto[]> | null | undefined;
  focusedGate?: GateKey | null;
  presentation?: 'default' | 'investigationCards';
  /** When true with investigationCards, omit the “{Gate} checks” heading (parent supplies title). */
  hideSectionTitle?: boolean;
  /** Verification evidence (and requestSnapshot) used to show compared inputs. */
  evidence?: Record<string, unknown> | null;
};

export function GateCheckpointAuditSection({
  audit,
  focusedGate,
  presentation = 'default',
  hideSectionTitle = false,
  evidence = null,
}: Props) {
  const theme = useTheme();
  if (!audit || Object.keys(audit).length === 0) {
    return (
      <AlertSoft>
        Step-level checkpoint text is unavailable for this run (verification completed before persistence shipped, or pipeline did
        not emit checkpoints). Use the gate summaries below or raw JSON.
      </AlertSoft>
    );
  }

  if (presentation === 'investigationCards') {
    const gate = focusedGate ?? (Object.keys(audit)[0] as GateKey | undefined);
    const steps = gate ? audit[gate] : undefined;
    if (!gate || !steps?.length) {
      return (
        <AlertSoft>No checkpoint steps stored for the selected gate.</AlertSoft>
      );
    }

    return (
      <Stack spacing={1.25}>
        {!hideSectionTitle ? (
          <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
            {gateMeta[gate].title} checks
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2,
          }}
        >
          {steps.map((step) => {
            const u = resolvedCheckpointDisplayStatus(step);
            const icon =
              u === 'PASS' ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 22, color: 'success.main' }} />
              ) : u === 'FAIL' ? (
                <CancelRoundedIcon sx={{ fontSize: 22, color: 'error.main' }} />
              ) : u === 'REVIEW' ? (
                <WarningAmberRoundedIcon sx={{ fontSize: 22, color: 'warning.main' }} />
              ) : (
                <WarningAmberRoundedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
              );
            const fullReason = formatGateReasonReadableFlow(step.reason).trim() || '—';
            const summary = truncateSummary(step.reason);
            const showTip = fullReason !== '—' && fullReason.length > summary.length;
            const compared = comparedValuesForCheckpointStep(step.step, evidence);
            const body = (
              <Box sx={{ mt: 0.75 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.55, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
                >
                  {fullReason}
                </Typography>
                {compared.length > 0 ? (
                  <Stack spacing={0.35} sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, letterSpacing: 0.02, textTransform: 'uppercase' }}
                    >
                      Compared
                    </Typography>
                    {compared.map((row) => (
                      <Typography
                        key={`${step.step}-${row.label}`}
                        variant="body2"
                        sx={{ lineHeight: 1.45, wordBreak: 'break-word' }}
                      >
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {row.label}:
                        </Box>{' '}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {row.value}
                        </Box>
                      </Typography>
                    ))}
                  </Stack>
                ) : null}
              </Box>
            );
            return (
              <Box
                key={`${gate}-${step.step}`}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.85),
                  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
                  // Same white paper fill for every checkpoint — status is icon-only.
                  bgcolor: 'background.paper',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Box sx={{ pt: 0.15, flexShrink: 0 }}>{icon}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                      {formatGateStepLabel(step.step)}
                      {step.isHardStop ? (
                        <Typography component="span" variant="caption" color="error" sx={{ ml: 0.75 }}>
                          Hard stop
                        </Typography>
                      ) : null}
                    </Typography>
                    {showTip ? (
                      <Tooltip title={fullReason} placement="top" arrow>
                        {body}
                      </Tooltip>
                    ) : (
                      body
                    )}
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        All checkpoint scenarios (Pass / Fail / Review)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        Every sub-step inside each gate, in verifier order — same wording the service used when evaluating this
        application. The overview above summarizes each gate; this section is the full drill-down.
      </Typography>
      {(focusedGate ? [focusedGate, ...GATE_ORDER_PIPE.filter((g) => g !== focusedGate)] : GATE_ORDER_PIPE).map((gate) => {
        const steps = audit[gate];
        if (!steps?.length) return null;
        const meta = gateMeta[gate];
        const selected = focusedGate === gate;
        return (
          <Box
            key={gate}
            sx={{
              border: '1px solid',
              borderColor: selected ? alpha(theme.palette.primary.main, 0.65) : alpha(theme.palette.divider, 0.9),
              borderRadius: 2,
              p: 1.25,
              bgcolor: 'background.paper',
              boxShadow: selected ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              {meta.short} · {meta.title}
              {selected ? ' · Selected gate' : ''}
            </Typography>
            <Stack spacing={1}>
              {steps.map((step) => {
                const resolvedStatus = resolvedCheckpointDisplayStatus(step);
                return (
                <Stack
                  key={`${gate}-${step.step}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.5, sm: 1 }}
                  alignItems={{ sm: 'flex-start' }}
                >
                  <Chip
                    label={resolvedStatus}
                    color={checkpointChipColor(resolvedStatus)}
                    size="small"
                    sx={{ minWidth: 72, fontWeight: 700, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatGateStepLabel(step.step)}
                      {step.isHardStop ? (
                        <Typography component="span" variant="caption" color="error" sx={{ ml: 0.75 }}>
                          Hard stop
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.35,
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {formatGateReasonReadableFlow(step.reason)}
                    </Typography>
                    {(() => {
                      const compared = comparedValuesForCheckpointStep(step.step, evidence);
                      if (!compared.length) return null;
                      return (
                        <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                          {compared.map((row) => (
                            <Typography
                              key={`${gate}-${step.step}-${row.label}`}
                              variant="caption"
                              sx={{ display: 'block', lineHeight: 1.45, wordBreak: 'break-word' }}
                            >
                              <Box component="span" color="text.secondary">
                                {row.label}:
                              </Box>{' '}
                              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {row.value}
                              </Box>
                            </Typography>
                          ))}
                        </Stack>
                      );
                    })()}
                  </Box>
                </Stack>
              );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function AlertSoft({ children }: { children: ReactNode }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.text.primary, 0.03),
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
}
